// The canvas store — the single source of truth for what's on each canvas.
// react-grid-layout renders from here; every mutation flows through here. Two
// independent sections (§7B), so placements + flags are keyed by section.
//
// The pure logic lives in placements.ts (unit-tested); this file is just the
// zustand state + thin actions that call it. `dirty` drives the Save button;
// `loaded` guards the one-time hydrate from the backend per section.

import { create } from "zustand";
import type { LayoutSection } from "@catbento/shared";
import {
  placeNewWidget,
  mergeRglLayout,
  fromPlacements,
  type PlacedWidget,
  type RglLayoutItem,
} from "./placements.js";
import { getWidget } from "../widgets/registry.js";
import type { StatsConfig } from "../widgets/statsQuery.js";
import type { WidgetPlacement } from "@catbento/shared";

type BySection<T> = Record<LayoutSection, T>;

const emptyBySection = <T,>(value: () => T): BySection<T> => ({
  leagues: value(),
  players: value(),
});

/** A fresh instance id. crypto.randomUUID is available in every browser we
 *  target; kept here (not in the pure module) so placements.ts stays DOM-free. */
const genId = (): string => crypto.randomUUID();

interface CanvasState {
  placements: BySection<PlacedWidget[]>;
  /** True once a section has been hydrated from the backend (guards re-hydrate). */
  loaded: BySection<boolean>;
  /** True when a section has unsaved changes (enables its Save button). */
  dirty: BySection<boolean>;
  /** Per-widget query-drawer settings (FE4), keyed by the widget's session-only
   *  instanceId (globally unique, so not nested by section). SESSION-ONLY: it
   *  survives tab switches and re-renders but not a reload — the save path only
   *  serializes { widgetKey, position }, and a reload re-mints instanceIds. The
   *  "remember forever" version is a deferred backend slice (frontend-plan §5.3). */
  configs: Record<string, StatsConfig>;

  /** Hydrate a section from a loaded board — replaces placements, NOT dirty. */
  hydrate: (section: LayoutSection, placements: WidgetPlacement[]) => void;
  /** Add a widget to a section (dropped below existing, default size). */
  addWidget: (section: LayoutSection, widgetKey: string) => void;
  /** Remove one placed widget instance. */
  removeWidget: (section: LayoutSection, instanceId: string) => void;
  /** Apply a react-grid-layout drag/resize result. */
  applyLayout: (section: LayoutSection, layout: RglLayoutItem[]) => void;
  /** Set a widget's query-drawer settings (FE4). Doesn't touch `dirty` — config
   *  is session-only, not part of the saved board. */
  setConfig: (instanceId: string, config: StatsConfig) => void;
  /** Mark a section clean after a successful save. */
  markSaved: (section: LayoutSection) => void;
  /** Wipe all in-memory canvas state. Called on sign-out so the next user (or
   *  the next login) starts clean and re-hydrates from the backend — otherwise
   *  the module-level store outlives the session and an UNSAVED canvas would
   *  reappear after logout/login (the `loaded` guard also blocks re-hydrate). */
  reset: () => void;
}

export const useCanvasStore = create<CanvasState>((set) => ({
  placements: emptyBySection<PlacedWidget[]>(() => []),
  loaded: emptyBySection(() => false),
  dirty: emptyBySection(() => false),
  configs: {},

  hydrate: (section, placements) =>
    set((s) => ({
      placements: { ...s.placements, [section]: fromPlacements(placements, genId) },
      loaded: { ...s.loaded, [section]: true },
      dirty: { ...s.dirty, [section]: false },
    })),

  addWidget: (section, widgetKey) =>
    set((s) => {
      const def = getWidget(widgetKey);
      if (!def) return {}; // unknown key — ignore (palette only offers known ones)
      const current = s.placements[section];
      const placed: PlacedWidget = {
        instanceId: genId(),
        widgetKey,
        position: placeNewWidget(current, def.defaultSize),
      };
      return {
        placements: { ...s.placements, [section]: [...current, placed] },
        dirty: { ...s.dirty, [section]: true },
      };
    }),

  removeWidget: (section, instanceId) =>
    set((s) => {
      // Drop any query-drawer config the removed instance owned (no leak).
      const configs = { ...s.configs };
      delete configs[instanceId];
      return {
        placements: {
          ...s.placements,
          [section]: s.placements[section].filter((p) => p.instanceId !== instanceId),
        },
        dirty: { ...s.dirty, [section]: true },
        configs,
      };
    }),

  applyLayout: (section, layout) =>
    set((s) => {
      const next = mergeRglLayout(s.placements[section], layout);
      // Only dirty if a position actually changed — RGL fires onLayoutChange on
      // mount too, and we don't want that to light up the Save button.
      const changed = next.some((p, i) => {
        const prev = s.placements[section][i];
        return (
          !prev ||
          prev.position.x !== p.position.x ||
          prev.position.y !== p.position.y ||
          prev.position.w !== p.position.w ||
          prev.position.h !== p.position.h
        );
      });
      if (!changed) return {};
      return {
        placements: { ...s.placements, [section]: next },
        dirty: { ...s.dirty, [section]: true },
      };
    }),

  setConfig: (instanceId, config) =>
    set((s) => ({ configs: { ...s.configs, [instanceId]: config } })),

  markSaved: (section) =>
    set((s) => ({ dirty: { ...s.dirty, [section]: false } })),

  reset: () =>
    set({
      placements: emptyBySection<PlacedWidget[]>(() => []),
      loaded: emptyBySection(() => false),
      dirty: emptyBySection(() => false),
      configs: {},
    }),
}));
